import i18next from "i18next";

import { getFormattedDate } from "./getFormattedDate";

/**
 * Calculates the relative time difference between a given date and the current time.
 * Returns a localized string such as "5 minutes ago", "Yesterday", or "2 days ago".
 *
 * For recent events (< 24 hours), uses time-based thresholds regardless of calendar day boundaries.
 * For older events, uses calendar-day based logic (e.g., "Yesterday" for previous calendar day).
 *
 * @param date - The `Date` object to calculate the relative time from.
 * @param returnDateAfterDays - Optional. If the difference in days is greater than this value, returns the specific date using `getFormattedDate`.
 * @param locale - Optional. One of the six supported tags. Defaults to the
 * ambient i18next language. A memoized caller must pass it, see EAH-2135.
 * @returns A localized string representing the relative time difference.
 *
 * @example
 * ```ts
 * getRelativeTime(new Date(Date.now() - 45000)); // "45 seconds ago"
 * getRelativeTime(new Date(Date.now() - 60000)); // "1 minute ago"
 * getRelativeTime(new Date(Date.now() - 3600000)); // "1 hour ago"
 * getRelativeTime(new Date(Date.now() - 86400000)); // "Yesterday" (if previous calendar day)
 * getRelativeTime(new Date(Date.now() - 172800000)); // "2 days ago"
 * ```
 */
export const getRelativeTime = (
  date: Date,
  returnDateAfterDays?: number,
  /**
   * Language to translate in. Omit and the ambient i18next instance decides.
   * A component must pass it: React Compiler caches this call on the arguments
   * it can see, so without the locale among them a language change returns the
   * cached string (EAH-2135).
   */
  locale?: string,
  /** The clock to measure against; a list passes one shared value so a memoized row re-reads its age. */
  now: Date = new Date(),
): string => {
  const lng = locale;
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  // 🔹 Seconds ago
  if (diffInSeconds < 60) {
    return i18next.t("date.secondsAgo", { lng, count: diffInSeconds });
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  // 🔹 Minutes ago
  if (diffInMinutes < 60) {
    return i18next.t("date.minutesAgo", { lng, count: diffInMinutes });
  }

  // 🔹 Check for Yesterday (calendar day based)
  // Note: This check happens after minutes/hours for recent events
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const inputDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  if (inputDate.getTime() === yesterday.getTime()) {
    return i18next.t("date.yesterday", { lng });
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  // 🔹 Hours ago
  if (diffInHours < 24) {
    return i18next.t("date.hoursAgo", { lng, count: diffInHours });
  }

  const diffInDays = Math.floor(diffInHours / 24);

  // 🔹 Return specific date if threshold met
  if (returnDateAfterDays && diffInDays > returnDateAfterDays) {
    return getFormattedDate(date, lng ?? i18next.language);
  }

  // 🔹 Days ago (2+ days ago)
  if (diffInDays < 7) {
    return i18next.t("date.daysAgo", { lng, count: diffInDays });
  }

  const diffInWeeks = Math.floor(diffInDays / 7);

  // 🔹 If it's 1-3 weeks, return "weeks ago"
  if (diffInWeeks >= 1 && diffInDays < 26) {
    return i18next.t("date.weeksAgo", { lng, count: diffInWeeks });
  }

  // 🔹 If it's between 26-29 days, return "Less than a month ago"
  if (diffInDays >= 26 && diffInDays < 30) {
    return i18next.t("date.lessThanMonth", { lng });
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  // 🔹 Months ago
  if (diffInMonths < 12) {
    return i18next.t("date.monthsAgo", { lng, count: diffInMonths });
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  // 🔹 Years ago
  return i18next.t("date.yearsAgo", { lng, count: diffInYears });
};
